import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Field, Formik, Form } from "formik";
import { discountSchema } from "../../utils/validation.util";
import { largeModalShowHide } from "../../features/modalSlice";
import {
  createDiscount,
  updateDiscount,
  fetchItemsForDiscount,
} from "../../features/discountSlice";
import { fetchHuntPackList } from "../../features/huntPackSlice";
import { fetchQuestPackList } from "../../features/questPackSlice";
import { InputError, LevelReq } from "../../components/form/FormBody";
import DateInput from "../../components/form/DateInput";
import { errorToastify } from "../../utils/toastify.util.js";

const PACK_TYPE = {
  ITEM: "0",
  HUNT: "1",
  QUEST: "2",
};

/** ✅ Searchable Multi-Select Dropdown */
const MultiSearchSelectDropdown = ({
  label,
  placeholder = "Search...",
  options = [],
  values = [],
  onChange,
  disabled = false,
  colClass = "col-md-10",
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const wrapRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const normalizedValues = useMemo(
    () =>
      Array.isArray(values)
        ? values
            .filter(
              (v) =>
                v !== undefined &&
                v !== null &&
                v !== "" &&
                String(v).toLowerCase() !== "undefined" &&
                String(v).toLowerCase() !== "null",
            )
            .map(String)
        : [],
    [values],
  );

  const filtered = useMemo(() => {
    const q = (search || "").trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) =>
      String(o?.name || "")
        .toLowerCase()
        .includes(q),
    );
  }, [options, search]);

  const selectedLabels = useMemo(() => {
    const map = new Map(options.map((o) => [String(o.id), o.name]));
    const labels = normalizedValues.map(
      (id) => map.get(String(id)) || `Selected (${id})`,
    );
    return labels;
  }, [normalizedValues, options]);

  const toggleValue = (id) => {
    const strId = String(id);
    const next = normalizedValues.includes(strId)
      ? normalizedValues.filter((v) => v !== strId)
      : [...normalizedValues, strId];
    onChange(next);
  };

  const summary =
    selectedLabels.length === 0
      ? `Select ${label}`
      : selectedLabels.length <= 2
        ? selectedLabels.join(", ")
        : `${selectedLabels.slice(0, 2).join(", ")} +${
            selectedLabels.length - 2
          } more`;

  return (
    <div className={`${colClass} discount-pack-dd`} ref={wrapRef}>
      <LevelReq className="form-label fs-7">{label}</LevelReq>

      <div className="category-dd">
        <div
          className={`form-control category-dd-input ${
            disabled ? "bg-light" : ""
          }`}
          role="button"
          onClick={() => {
            if (disabled) return;
            setOpen((v) => !v);
          }}
          style={{
            pointerEvents: disabled ? "none" : "auto",
            opacity: disabled ? 0.7 : 1,
          }}
        >
          <span>{summary}</span>
          <span className="category-dd-arrow">{open ? "▲" : "▼"}</span>
        </div>

        {open && (
          <div className="category-dd-panel discount-pack-dd-panel">
            <input
              type="text"
              className="form-control mb-2"
              placeholder={placeholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />

            <div className="category-dd-list discount-pack-dd-list">
              {filtered.length === 0 ? (
                <div className="text-muted small">No data found</div>
              ) : (
                <div className="category-dd-grid">
                  {filtered.map((item) => {
                    const checked = normalizedValues.includes(String(item.id));
                    return (
                      <div
                        key={item.id}
                        className="btn btn-light btn-sm text-start w-100 d-flex align-items-center justify-content-between"
                        style={{ marginBottom: 6, border: "1px solid #eee" }}
                        role="button"
                        tabIndex={0}
                        onClick={() => toggleValue(item.id)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            toggleValue(item.id);
                          }
                        }}
                      >
                        <div>{item.name}</div>
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={checked}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => toggleValue(item.id)}
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="category-dd-footer d-flex justify-content-between">
              <span className="small text-muted">
                {normalizedValues.length} selected
              </span>
              <button
                type="button"
                className="btn btn-sm btn-secondary"
                onClick={() => setOpen(false)}
              >
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/** ✅ helper (no hooks) */
const mergeOptionsWithSelectedMulti = (options, selectedValues = []) => {
  const map = new Map((options || []).map((o) => [Number(o.id), o]));
  (selectedValues || []).forEach((sv) => {
    if (
      sv === undefined ||
      sv === null ||
      sv === "" ||
      String(sv).toLowerCase() === "undefined" ||
      String(sv).toLowerCase() === "null"
    ) {
      return;
    }
    const id = Number(sv);
    if (Number.isNaN(id)) return;
    if (!map.has(id)) {
      map.set(id, { id, name: `Selected (${id})` });
    }
  });
  return Array.from(map.values());
};

const normalizePackIds = (val) => {
  const isValidId = (v) =>
    v !== undefined &&
    v !== null &&
    v !== "" &&
    String(v).toLowerCase() !== "undefined" &&
    String(v).toLowerCase() !== "null";

  return Array.isArray(val) ? val.filter(isValidId).map(String) : [];
};

const normalizeDateForInput = (value) => {
  if (!value) return "";
  const str = String(value);
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
  if (str.includes("T")) return str.split("T")[0];

  const parsed = new Date(str);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toISOString().split("T")[0];
};

const DiscountModal = () => {
  const dispatch = useDispatch();

  const {
    discountFormData,
    isUpdate,
    type,
    isSpin,
    itemsList,
    isLoadingItems,
  } = useSelector((state) => state.discount);

  const { listData: huntPackList, isLoad: isLoadingHuntPacks } = useSelector(
    (state) => state.huntPack,
  );
  const { listData: questPackList, isLoad: isLoadingQuestPacks } =
    useSelector((state) => state.questPack);

  // key tracks when we should remount Formik (on a new record being loaded)
  const [formKey, setFormKey] = useState(0);

  // bump key when discountFormData.id changes (new edit or fresh form)
  useEffect(() => {
    setFormKey((k) => k + 1);
  }, [discountFormData.id]);

  useEffect(() => {
    dispatch(fetchItemsForDiscount({ limit: 1000, offset: 0 }));
    dispatch(fetchHuntPackList({ limit: 1000, offset: 0 }));
    dispatch(fetchQuestPackList({ limit: 1000, offset: 0 }));
  }, [dispatch]);

  useEffect(() => {
    if (isUpdate) dispatch(largeModalShowHide());
  }, [isUpdate, dispatch]);

  const itemOptions = useMemo(() => {
    const list = Array.isArray(itemsList) ? itemsList : [];
    return list.map((x) => ({
      id: x.id,
      name: x.group_name || x.name || `Challenge pack ${x.id}`,
    }));
  }, [itemsList]);

  const huntOptions = useMemo(() => {
    const list = Array.isArray(huntPackList) ? huntPackList : [];
    return list.map((x) => ({
      id: x.id,
      name: x.title || x.name || `Hunt Pack ${x.id}`,
    }));
  }, [huntPackList]);

  const questOptions = useMemo(() => {
    const list = Array.isArray(questPackList) ? questPackList : [];
    return list.map((x) => ({
      id: x.id,
      name: x.name || x.title || `Quest Pack ${x.id}`,
    }));
  }, [questPackList]);

  const applyServerErrors = (message, setFieldError) => {
    if (!message) return;
    const lower = String(message).toLowerCase();
    if (
      lower.includes("pack") ||
      lower.includes("item") ||
      lower.includes("hunt") ||
      lower.includes("quest")
    ) {
      setFieldError("item_pack_ids", message);
      return;
    }
    if (lower.includes("date") || lower.includes("expir") || lower.includes("valid")) {
      setFieldError("end_date", message);
      setFieldError("start_date", message);
      return;
    }
    setFieldError("discount_code", message);
  };

  const onSubmitHandler = async (values, { setFieldError, validateForm, setTouched }) => {
    const errors = await validateForm();
    if (Object.keys(errors).length) {
      setTouched(
        {
          discount_name: true,
          discount_code: true,
          amount: true,
          max_users: true,
          start_date: true,
          end_date: true,
          item_pack_ids: true,
        },
        true,
      );
      return;
    }

    const items = [
      ...normalizePackIds(values.item_pack_ids).map((id) => ({
        id: Number(id),
        pack_type: PACK_TYPE.ITEM,
      })),
      ...normalizePackIds(values.hunt_pack_ids).map((id) => ({
        id: Number(id),
        pack_type: PACK_TYPE.HUNT,
      })),
      ...normalizePackIds(values.quest_pack_ids).map((id) => ({
        id: Number(id),
        pack_type: PACK_TYPE.QUEST,
      })),
    ];

    const payload = {
      ...values,
      items,
      start_date: values.start_date,
      end_date: values.end_date,
    };

    try {
      if (payload.id && payload.id !== "") {
        await dispatch(updateDiscount(payload)).unwrap();
      } else {
        delete payload.id;
        dispatch(createDiscount(payload));
      }
    } catch (err) {
      const message =
        err?.message || err?.mesasge || "Failed to update discount";
      applyServerErrors(message, setFieldError);
      errorToastify(message);
    }
  };

  return (
    <div className="container discount-modal-form">
      <Formik
        key={formKey}
        initialValues={{
          ...discountFormData,
          item_pack_ids: normalizePackIds(discountFormData?.item_pack_ids),
          hunt_pack_ids: normalizePackIds(discountFormData?.hunt_pack_ids),
          quest_pack_ids: normalizePackIds(discountFormData?.quest_pack_ids),
          start_date: normalizeDateForInput(discountFormData?.start_date),
          end_date: normalizeDateForInput(discountFormData?.end_date),
        }}
        validationSchema={discountSchema}
        onSubmit={onSubmitHandler}
      >
        {({ values, setFieldValue, setFieldTouched }) => {
          const mergedItemOptions = mergeOptionsWithSelectedMulti(
            itemOptions,
            values.item_pack_ids,
          );
          const mergedHuntOptions = mergeOptionsWithSelectedMulti(
            huntOptions,
            values.hunt_pack_ids,
          );
          const mergedQuestOptions = mergeOptionsWithSelectedMulti(
            questOptions,
            values.quest_pack_ids,
          );

          const onPackChange = (field, val) => {
            setFieldValue(field, val);
            setFieldTouched("item_pack_ids", true, false);
          };

          return (
            <Form>
              <div className="row mb-3">
                <div className="col-md-6">
                  <LevelReq htmlFor="discount_name" className="form-label fs-7">
                    Discount Title
                  </LevelReq>
                  <Field
                    type="text"
                    className="form-control"
                    id="discount_name"
                    name="discount_name"
                  />
                  <InputError name="discount_name" />
                </div>

                <div className="col-md-6">
                  <LevelReq htmlFor="discount_code" className="form-label fs-7">
                    Discount Code
                  </LevelReq>
                  <Field
                    type="text"
                    className="form-control"
                    id="discount_code"
                    name="discount_code"
                  />
                  <InputError name="discount_code" />
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-6">
                  <LevelReq htmlFor="amount" className="form-label fs-7">
                    Discount Value
                  </LevelReq>
                  <Field
                    type="text"
                    className="form-control"
                    id="amount"
                    name="amount"
                  />
                  <InputError name="amount" />
                </div>

                <div className="col-md-6">
                  <LevelReq htmlFor="max_users" className="form-label fs-7">
                    Max Users
                  </LevelReq>
                  <Field
                    type="number"
                    className="form-control"
                    id="max_users"
                    name="max_users"
                    min="1"
                  />
                  <InputError name="max_users" />
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-6">
                  <LevelReq htmlFor="start_date" className="form-label fs-7">
                    Start Date
                  </LevelReq>
                  <Field name="start_date">
                    {({ field, form }) => (
                      <DateInput
                        className="form-control"
                        id="start_date"
                        name={field.name}
                        value={field.value || ""}
                        onChange={(next) => form.setFieldValue(field.name, next)}
                        onBlur={field.onBlur}
                      />
                    )}
                  </Field>
                  <InputError name="start_date" />
                </div>

                <div className="col-md-6">
                  <LevelReq htmlFor="end_date" className="form-label fs-7">
                    End Date
                  </LevelReq>
                  <Field name="end_date">
                    {({ field, form }) => (
                      <DateInput
                        className="form-control"
                        id="end_date"
                        name={field.name}
                        value={field.value || ""}
                        onChange={(next) => form.setFieldValue(field.name, next)}
                        onBlur={field.onBlur}
                      />
                    )}
                  </Field>
                  <InputError name="end_date" />
                </div>
              </div>

              {/* ✅ Searchable multi-select per pack type, three across on the wide modal */}
              <div className="row mb-3 discount-pack-row">
                <MultiSearchSelectDropdown
                  label="Challenge Packs"
                  placeholder={
                    isLoadingItems ? "Loading..." : "Search challenge packs..."
                  }
                  options={mergedItemOptions}
                  values={values.item_pack_ids}
                  disabled={isLoadingItems}
                  onChange={(val) => onPackChange("item_pack_ids", val)}
                  colClass="col-md-4"
                />
                <MultiSearchSelectDropdown
                  label="Hunt Packs"
                  placeholder={
                    isLoadingHuntPacks ? "Loading..." : "Search hunt packs..."
                  }
                  options={mergedHuntOptions}
                  values={values.hunt_pack_ids}
                  disabled={isLoadingHuntPacks}
                  onChange={(val) => onPackChange("hunt_pack_ids", val)}
                  colClass="col-md-4"
                />
                <MultiSearchSelectDropdown
                  label="Quest Packs"
                  placeholder={
                    isLoadingQuestPacks ? "Loading..." : "Search quest packs..."
                  }
                  options={mergedQuestOptions}
                  values={values.quest_pack_ids}
                  disabled={isLoadingQuestPacks}
                  onChange={(val) => onPackChange("quest_pack_ids", val)}
                  colClass="col-md-4"
                />
                <div className="col-12">
                  <InputError name="item_pack_ids" />
                </div>
              </div>

              {/* Radio - Discount type */}
              <div className="row mb-3">
                <div className="col-md-12">
                  <label className="form-label fs-7">Discount Type</label>
                  <div>
                    {type?.map((item) => (
                      <label
                        key={`type-${item.value}`}
                        className="form-check-label me-3"
                      >
                        <Field
                          type="radio"
                          name="discount_type"
                          value={item.value}
                          className="form-check-input"
                        />
                        &nbsp;{item.label}
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <Field type="hidden" id="id" name="id" />

              <div className="col-md-4 mx-auto mb-3">
                <button
                  className="btn btn-primary w-100"
                  type={isSpin ? "button" : "submit"}
                  disabled={isSpin}
                >
                  {isSpin ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Loading...
                    </>
                  ) : (
                    <>
                      {values?.id ? "Edit Discount Data" : "Add Discount Data"}
                    </>
                  )}
                </button>
              </div>
            </Form>
          );
        }}
      </Formik>

      {/* ST-645: keep pack dropdowns inside the dialog */}
      <style>{`
        .discount-modal-form .discount-pack-row {
          position: relative;
          overflow: visible;
        }
        .discount-modal-form .discount-pack-dd {
          position: relative;
        }
        .discount-modal-form .discount-pack-dd-panel {
          position: absolute;
          bottom: calc(100% + 6px);
          left: 0;
          right: 0;
          width: 100%;
          z-index: 1050;
          max-height: min(260px, 35vh);
          display: flex;
          flex-direction: column;
          overflow: hidden;
          background: #ffffff;
          border: 1px solid rgba(0, 0, 0, 0.15);
          border-radius: 8px;
          box-shadow: 0 -8px 24px rgba(0, 0, 0, 0.15);
          padding: 10px;
        }
        .discount-modal-form .discount-pack-dd-list {
          max-height: 180px;
          overflow-y: auto;
          flex: 1 1 auto;
        }
        .discount-modal-form .category-dd-grid {
          grid-template-columns: 1fr;
        }
      `}</style>
    </div>
  );
};

export default DiscountModal;
